/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './devices-map.scss';

/* eslint-disable import/no-unresolved, import/default, import/namespace */

import PrototypeMap from "./prototypeMap";
import spearMarkerSvg from '../../svg/spear_marker.svg';

/* eslint-enable import/no-unresolved, import/default, import/namespace */

export default angular.module('agrolog.farmer.devicesMap', [])
    .directive('devicesMap', DevicesMap)
    .name;

/*@ngInject*/
function DevicesMap () {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            mapId: '<',
            singleDevice: "<"
        },
        controller: DevicesMapController,
        controllerAs: 'vm'
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function DevicesMapController($scope, $log, $element, $window,  types, toast, agTypes, unitService, userService, attributeService, agDeviceService) {

    let vm = this;
    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' || userService.getAuthority() === 'CUSTOMER_USER';

    vm.devices = [];
    vm.currentDevice = null;
    vm.loading = false;
    vm.tempHistoryUnits = unitService.temperatureUnitSymbol();
    let deviceMapContainer;

    vm.getCurrentLocation = getCurrentLocation;

    vm.latestValuesSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: (subscription) => {
                vm.loading = subscription.loadingData;
            }
        },
        useDashboardTimewindow: false
    };

    $scope.$watch('vm.ctx', () => {
        if (vm.showData && vm.ctx) {
            reset();
            deviceMapContainer = angular.element('#' + vm.mapId, $element);
            addResizeListener(deviceMapContainer[0], () => { // eslint-disable-line no-undef
                if (vm.ctx.map) {
                    vm.ctx.map.resize();
                }
            });
            if (vm.mapId === "devices-map") {
                reloadDevices();
            }
        }
    });

    $scope.$watch('vm.singleDevice', (newValue) => {
        if (newValue) {
            reset();
            createLatestValuesSubscription();
        }
    });

    function reloadDevices(keepSelection) {
        vm.devices = [];
        if (!keepSelection) {
            vm.currentDevice = null;
        }
        agDeviceService.getDevicesByType(agTypes.farmerDeviceTypes.cellularSensorSpear.value).then(
            (devices) => {
                vm.devices = devices;
                vm.loading = false;
                createLatestValuesSubscription();
            },
            () => {
                vm.devices = [];
                vm.loading = false;
            }
        );

    }

    function createLatestValuesSubscription() {
        let latestValuesSubscriptionsInfo = [];
        if (vm.devices.length) {
            for (let i = 0; i < vm.devices.length; i++) {
                let subscriptionObj = {
                    type: types.datasourceType.entity,
                    entityType: types.entityType.device,
                    entityId: vm.devices[i].id.id,
                    entityName: vm.devices[i].name,
                    attributes: [
                        {name: agTypes.attributes.spearSensor.latitude},
                        {name: agTypes.attributes.spearSensor.longitude}
                    ],
                    timeseries: [
                        {
                            name: agTypes.telemetry.spearSensor.temperature,
                            postFuncBody: unitService.temperaturePostprocessingFuncBody()
                        },
                        {
                            name: agTypes.telemetry.spearSensor.ambientTemperature,
                            postFuncBody: unitService.temperaturePostprocessingFuncBody()
                        }
                    ]
                };
                latestValuesSubscriptionsInfo.push(subscriptionObj);
            }
        } else if (vm.singleDevice) {
            latestValuesSubscriptionsInfo.push(
                {
                    type: types.datasourceType.entity,
                    entityType: types.entityType.device,
                    entityId: vm.singleDevice.id.id,
                    entityName: vm.singleDevice.name,
                    attributes: [
                        {name: agTypes.attributes.spearSensor.latitude},
                        {name: agTypes.attributes.spearSensor.longitude}
                    ],
                    timeseries: [
                        {
                            name: agTypes.telemetry.spearSensor.temperature,
                            postFuncBody: unitService.temperaturePostprocessingFuncBody()
                        },
                        {
                            name: agTypes.telemetry.spearSensor.ambientTemperature,
                            postFuncBody: unitService.temperaturePostprocessingFuncBody()
                        }
                    ]
                }
            );
        }
        if (latestValuesSubscriptionsInfo.length) {
            vm.ctx.subscriptionApi.createSubscriptionFromInfo(types.widgetType.latest.value, latestValuesSubscriptionsInfo, vm.latestValuesSubscriptionOptions, false, true).then(
                (subscription) => {
                    vm.ctx.defaultSubscription = subscription;
                    initMap();
                }
            )
        }
    }

    function initMap() {
        let settings = {
            autocloseTooltip: true,
            fitMapBounds: true,
            label: '<div style="position: relative; white-space: nowrap; text-align: center; font-size: 12px; top: 4px;"><span style="margin-left: -500%;"></span><span style="border: 2px solid #514f9d; border-radius: 10px; color: #514f9d; background-color: #fff; padding-left: 5px; padding-right: 5px; padding-top: 3px; padding-bottom: 3px;">${entityName}</span><span style="margin-right: -500%;"></span></div>',
            latKeyName: "latitude",
            lngKeyName: "longitude",
            mapProvider: "OpenStreetMap.Mapnik",
            markerImage: spearMarkerSvg,
            markerImageSize: 60,
            showLabel: true,
            showTooltip: true,
            tooltipPattern: "<b>${entityName}</b><br/><br/><b>Temperature:</b><span style='color:#ed6d05;font-weight:600'> ${temperature_1} " + vm.tempHistoryUnits + "</span><br/><b>Ambient temperature:</b><span style='color:#3498db;font-weight:600'> ${temperature_ambient} " + vm.tempHistoryUnits + "</span><br/><b>Latitude:</b> ${latitude:7}<br/><b>Longitude:</b> ${longitude:7}<br/><br/>",
            useClusterMarkers: true,
            zoomOnClick: true,
            showCoverageOnHover: true,
            animate: true,
            maxClusterRadius: 80,
            removeOutsideVisibleBounds: true
        };
        if (vm.mapId === "devices-map") {
            settings.tooltipPattern += "<link-act name='device_details'>Show details</link-act><br/>";
        }
        vm.ctx.settings = settings;
        if (vm.mapId === "devices-map") {
            vm.ctx.map = new PrototypeMap('openstreet-map', false, vm.ctx, false, deviceMapContainer, "roadMap");
        } else if (vm.mapId === "device-map") {
            vm.ctx.map = new PrototypeMap('openstreet-map', false, vm.ctx, false, deviceMapContainer, "satellite");
        }
        vm.ctx.map.update();
    }

    function onDataUpdated(subscription, apply) {
        if (vm.ctx.map) {
            vm.ctx.map.update();
        }
        if (apply) {
            $scope.$digest();
        }
    }

    function getCurrentLocation() {
        if ($window.navigator.geolocation) {
            $window.navigator.geolocation.getCurrentPosition(
                (position) => {
                    let attributes = [];
                    attributes.push({key: 'latitude', value: position.coords.latitude});
                    attributes.push({key: 'longitude', value: position.coords.longitude});
                    attributeService.saveEntityAttributes(vm.singleDevice.id.entityType, vm.singleDevice.id.id, types.attributesScope.server.value, attributes);
                },
                () => {
                    toast.showError("Geolocation is blocked in your browser", angular.element("#device-map"),'top left');
                },
                {
                    enableHighAccuracy: true
                }
            );
        } else {
            toast.showError("Your browser doesn't support geolocation", angular.element("#device-map"),'top left');
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }

    $scope.$on('$destroy', () => {
        reset();
    });

    function reset() {
        if (vm.ctx.defaultSubscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.ctx.defaultSubscription.id);
            vm.ctx.defaultSubscription = null;
            if (vm.ctx.map) {
                vm.ctx.map.removeMap();
            }
            vm.ctx.map = null;
        }
    }
}